//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { IterationWarningEvent } from '../../../types/streaming';

interface IterationWarningBannerProps {
  warning: IterationWarningEvent | null;
  onDismiss: () => void;
}

const levelClasses: Record<IterationWarningEvent['level'], string> = {
  info: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200',
  warning:
    'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  error: '', // handled by destructive variant
};

const IterationWarningBanner: React.FC<IterationWarningBannerProps> = ({
  warning,
  onDismiss,
}) => {
  if (!warning) return null;
  const isError = warning.level === 'error';

  return (
    <Alert
      data-testid="iteration-warning-banner"
      variant={isError ? 'destructive' : 'default'}
      className={cn(
        'mb-1 flex items-start justify-between',
        levelClasses[warning.level],
      )}
    >
      <AlertDescription className="flex-1">
        {warning.message} ({warning.iteration} / {warning.cap})
      </AlertDescription>
      {!isError && (
        <Button
          data-testid="iteration-warning-dismiss"
          variant="ghost"
          size="icon"
          className="ml-2 size-5 shrink-0 opacity-70 hover:opacity-100"
          aria-label="dismiss"
          onClick={onDismiss}
        >
          <X className="size-3" />
        </Button>
      )}
    </Alert>
  );
};

export default IterationWarningBanner;
